/**
 * PATH: apps/quote/src/components/builder/panels/takeoff-assist/RowNavigator.tsx
 * PURPOSE: Row navigator for jumping between sections and items
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight, ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { QuoteSection } from '@/lib/quoteApi'

export default function RowNavigator({
  sections, selectedSectionId, selectedItemId, onJumpToRow,
}: {
  sections: QuoteSection[]
  selectedSectionId?: string | null
  selectedItemId?: string | null
  onJumpToRow: (sectionId: string, itemId: string) => void
}) {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set())

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(sectionId)) { next.delete(sectionId) } else { next.add(sectionId) }
      return next
    })
  }

  return (
    <div className="space-y-1">
      {sections.map(section => {
        const isExpanded = expandedSections.has(section.id)
        const items = section.items || []

        return (
          <div key={section.id}>
            <button
              onClick={() => toggleSection(section.id)}
              className={cn(
                'w-full flex items-center gap-1.5 px-2 py-1.5 text-left rounded hover:bg-slate-100 dark:hover:bg-slate-800',
                selectedSectionId === section.id && 'bg-slate-100 dark:bg-slate-800'
              )}
            >
              {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-400" />}
              <span className="text-xs font-medium text-slate-700 dark:text-slate-300 truncate">{section.title}</span>
              <span className="text-[10px] text-slate-400 ml-auto">{items.length}</span>
            </button>

            {isExpanded && items.length > 0 && (
              <div className="ml-4 space-y-0.5 mt-1">
                {items.slice(0, 10).map((item, idx) => (
                  <button
                    key={item.id}
                    onClick={() => onJumpToRow(section.id, item.id)}
                    className={cn(
                      'w-full flex items-center gap-1.5 px-2 py-1 text-left rounded text-xs',
                      selectedItemId === item.id
                        ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                        : 'hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-600 dark:text-slate-400'
                    )}
                  >
                    <span className="text-slate-400 w-4">{idx + 1}</span>
                    <span className="truncate flex-1">{item.description || item.location || 'Untitled'}</span>
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100" />
                  </button>
                ))}
                {items.length > 10 && (
                  <p className="text-[10px] text-slate-400 px-2 py-1">+{items.length - 10} more rows</p>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
